import { useEffect, useRef, useState, type ReactNode } from 'react';
import { AlertTriangle, CheckCircle2, Eye, History, Loader2, Save } from 'lucide-react';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Textarea } from '@/components/ui/textarea';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { VIEWER_NO_PERMISSION } from '@/hooks/useIsViewer';
import { cn } from '@/lib/utils';
import toastService from '@/services/toast/service';
import { aiPreviewService, type EmailPreview, type PreviewScans, type PromptsOverview } from '@/services/aiPreview';
import { EmailCard } from './EmailTab';
import { failedWithoutMessage } from './shared';

/** A part of the wording people can change: the tone rules, the subject rules, or one email. */
interface Section {
  id: string;
  name: string;
  /** The wording keys it edits, as the API names them. */
  keys: Array<{ key: string; label: string; rows: number }>;
  audience?: string;
}

function sectionsOf(overview: PromptsOverview): { shared: Section[]; emails: Section[] } {
  return {
    shared: [
      { id: 'style', name: 'Tone rules', keys: [{ key: 'style', label: 'How to write it, with the example email', rows: 22 }] },
      { id: 'subject', name: 'Subject line', keys: [{ key: 'subject', label: 'Subject line rules', rows: 6 }] },
    ],
    emails: overview.emails.map((e) => ({
      id: e.key,
      name: e.name,
      audience: e.audience,
      keys: [
        { key: `${e.key}.where`, label: 'Where they are', rows: 4 },
        { key: `${e.key}.offer`, label: 'What to offer', rows: 6 },
      ],
    })),
  };
}

function defaultOf(overview: PromptsOverview, key: string): string {
  if (key === 'style') return overview.defaults.style;
  if (key === 'subject') return overview.defaults.subject;
  const [email, part] = key.split('.') as [string, 'where' | 'offer'];
  return overview.defaults.emails[email]?.[part] ?? '';
}

interface PromptsTabProps {
  overview: PromptsOverview;
  drafts: Record<string, string>;
  onDrafts: (drafts: Record<string, string>) => void;
  onOverview: (overview: PromptsOverview) => void;
  selected: string;
  onSelect: (id: string) => void;
  /** The customer picked above, to preview on; null when none is. */
  account: { publicId: string; name: string } | null;
  scans: PreviewScans;
  canRun: boolean;
  canSave: boolean;
  formatDate: (iso: string | null) => string | null;
}

export function PromptsTab({
  overview, drafts, onDrafts, onOverview, selected, onSelect, account, scans, canRun, canSave, formatDate,
}: PromptsTabProps) {
  const [saving, setSaving] = useState(false);
  const [previewing, setPreviewing] = useState(false);
  const [preview, setPreview] = useState<EmailPreview | null>(null);
  // A preview that finishes after another customer was picked belongs to the old one.
  const accountId = useRef(account?.publicId);
  accountId.current = account?.publicId;
  useEffect(() => {
    setPreview(null);
    setPreviewing(false);
  }, [account?.publicId]);

  const { shared, emails } = sectionsOf(overview);
  const section = [...shared, ...emails].find((s) => s.id === selected) ?? shared[0];

  const savedOf = (key: string) => overview.saved.find((s) => s.key === key);
  const baseOf = (key: string) => savedOf(key)?.text ?? defaultOf(overview, key);
  const valueOf = (key: string) => drafts[key] ?? baseOf(key);
  const isDirty = (key: string) => drafts[key] !== undefined && drafts[key] !== baseOf(key);
  const statusOf = (s: Section) =>
    s.keys.some((k) => isDirty(k.key)) ? 'unsaved' : s.keys.some((k) => savedOf(k.key)) ? 'saved' : 'default';

  const dirtyKeys = section.keys.filter((k) => isDirty(k.key)).map((k) => k.key);
  const savedKeys = section.keys.filter((k) => savedOf(k.key)).map((k) => k.key);
  const lastSaved = section.keys
    .map((k) => savedOf(k.key))
    .filter((s) => s !== undefined)
    .sort((a, b) => b.updatedAt.localeCompare(a.updatedAt))[0];

  const setDraft = (key: string, text: string) => onDrafts({ ...drafts, [key]: text });
  const dropDrafts = (keys: string[]) => onDrafts(Object.fromEntries(Object.entries(drafts).filter(([k]) => !keys.includes(k))));

  const save = async () => {
    setSaving(true);
    try {
      let next = overview;
      for (const key of dirtyKeys) {
        const text = drafts[key];
        // Back to the built-in text is a reset, not a copy of it.
        next = text.trim() === defaultOf(overview, key).trim()
          ? await aiPreviewService.resetWording(key)
          : await aiPreviewService.saveWording(key, text);
      }
      onOverview(next);
      dropDrafts(dirtyKeys);
      toastService.success('Saved for the email job');
    } catch {
      // The API client already shows the error.
    } finally {
      setSaving(false);
    }
  };

  const reset = async () => {
    setSaving(true);
    try {
      let next = overview;
      for (const key of savedKeys) next = await aiPreviewService.resetWording(key);
      onOverview(next);
      dropDrafts(section.keys.map((k) => k.key));
      toastService.success('Back to the default');
    } catch {
      // The API client already shows the error.
    } finally {
      setSaving(false);
    }
  };

  const runPreview = async () => {
    if (!account) return;
    const id = account.publicId;
    setPreviewing(true);
    try {
      const wording = Object.fromEntries(Object.entries(drafts).filter(([k]) => isDirty(k)));
      const emailKey = section.audience ? section.id : undefined;
      const result = await aiPreviewService.email(id, { scans, emailKey, wording });
      if (accountId.current === id) setPreview(result);
    } catch (err) {
      // The API client shows errors that carry a message.
      if (accountId.current === id && failedWithoutMessage(err)) toastService.error("The preview didn't finish. Try again.");
    } finally {
      if (accountId.current === id) setPreviewing(false);
    }
  };

  return (
    <div className="grid gap-5 lg:grid-cols-[240px_minmax(0,1fr)] items-start">
      <nav aria-label="Prompt parts" className="flex flex-col gap-4">
        {[
          { label: 'All emails', items: shared },
          { label: 'Emails', items: emails },
        ].map((group) => (
          <div key={group.label} className="flex flex-col gap-0.5">
            <span className="px-2.5 pb-1 text-xs font-medium text-muted-foreground">{group.label}</span>
            {group.items.map((s) => {
              const status = statusOf(s);
              return (
                <button
                  key={s.id}
                  type="button"
                  onClick={() => {
                    onSelect(s.id);
                    setPreview(null);
                  }}
                  className={cn(
                    'flex items-center justify-between gap-2 rounded-md px-2.5 py-2 text-left text-[13px] hover:bg-muted',
                    s.id === section.id && 'bg-muted font-medium',
                  )}
                >
                  <span className="min-w-0">{s.name}</span>
                  <Badge tone={status === 'unsaved' ? 'warning' : status === 'saved' ? 'info' : 'neutral'} className="shrink-0">
                    {status === 'unsaved' ? 'Not saved' : status === 'saved' ? 'Edited' : 'Default'}
                  </Badge>
                </button>
              );
            })}
          </div>
        ))}
      </nav>

      <div className="flex flex-col gap-5 min-w-0">
        <Card className="gap-4 px-5 py-4">
          <div className="flex flex-wrap items-center gap-2">
            <h2 className="text-base font-semibold">{section.name}</h2>
            {statusOf(section) === 'unsaved' && <Badge tone="warning">Edited, not saved</Badge>}
            {statusOf(section) === 'saved' && <Badge tone="info">Edited</Badge>}
          </div>
          {lastSaved && (
            <span className="-mt-2 text-xs text-muted-foreground">
              Saved{lastSaved.updatedBy ? ` by ${lastSaved.updatedBy}` : ''} on {formatDate(lastSaved.updatedAt)}
            </span>
          )}

          {section.audience && (
            <div className="flex flex-col gap-1">
              <span className="text-xs font-medium text-muted-foreground">Who gets it</span>
              <p className="text-[13px]">{section.audience}</p>
            </div>
          )}

          {section.keys.map((k) => (
            <div key={k.key} className="flex flex-col gap-1.5">
              <label htmlFor={`wording-${k.key}`} className="text-xs font-medium text-muted-foreground">
                {k.label}
              </label>
              <Textarea
                id={`wording-${k.key}`}
                value={valueOf(k.key)}
                rows={k.rows}
                onChange={(e) => setDraft(k.key, e.target.value)}
                className="font-mono text-[13px] leading-relaxed"
              />
            </div>
          ))}

          <div className="flex flex-col gap-1.5">
            <span className="text-xs font-medium text-muted-foreground">What the AI sees about the customer</span>
            <div className="flex flex-wrap gap-1.5">
              {overview.fields.map((f) => (
                <code key={f} className="rounded bg-muted px-1.5 py-0.5 text-xs">
                  {f}
                </code>
              ))}
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-2 pt-1">
            {dirtyKeys.length > 0 && (
              <Button variant="ghost" size="sm" disabled={saving} onClick={() => dropDrafts(dirtyKeys)}>
                Discard changes
              </Button>
            )}
            {savedKeys.length > 0 && (
              <AdminOnly allowed={canSave}>
                <Button variant="outline" size="sm" disabled={!canSave || saving} onClick={reset}>
                  <History />
                  Reset to default
                </Button>
              </AdminOnly>
            )}
            <div className="flex-1" />
            <Button variant="outline" size="sm" disabled={!account || !canRun || previewing} onClick={runPreview}>
              {previewing ? <Loader2 className="animate-spin" /> : <Eye />}
              {account ? `Preview on ${account.name}` : 'Pick a customer to preview'}
            </Button>
            <AdminOnly allowed={canSave}>
              <Button size="sm" disabled={!canSave || saving || dirtyKeys.length === 0} onClick={save}>
                {saving ? <Loader2 className="animate-spin" /> : <Save />}
                Save for the email job
              </Button>
            </AdminOnly>
          </div>
          <p className="text-xs text-muted-foreground leading-relaxed">
            Safety checks always run on what the AI writes: no links, phone numbers or email addresses, 35 to 90 words, no stock AI phrases. A draft that
            fails is held, never sent. Saving changes the email for every account from the job's next run.
          </p>
        </Card>

        {previewing && (
          <Card role="status" className="flex-row items-center justify-center gap-2 px-5 py-8 text-sm text-muted-foreground">
            <Loader2 className="size-4 animate-spin" />
            Writing a preview with your edits
          </Card>
        )}
        {preview && !previewing && (
          <div className="flex flex-col gap-3">
            <div className="flex items-center justify-between gap-2">
              <h3 className="text-sm font-semibold">Preview with your edits</h3>
              {preview.email &&
                (preview.email.problems.length === 0 ? (
                  <span className="flex items-center gap-1.5 text-[13px] font-medium text-emerald-700 dark:text-emerald-400">
                    <CheckCircle2 className="size-4" />
                    All checks passed
                  </span>
                ) : (
                  <span className="flex items-start gap-1.5 text-[13px] font-medium text-amber-700 dark:text-amber-400">
                    <AlertTriangle className="size-4 mt-0.5 shrink-0" />
                    Held: {preview.email.problems.join('; ')}
                  </span>
                ))}
            </div>
            {preview.email ? (
              <EmailCard
                from="Jovan · RoasForm"
                to={preview.to}
                subject={preview.email.subject}
                body={preview.email.body}
                bookingUrl={preview.bookingUrl}
              />
            ) : (
              <Card className="px-5 py-6 text-sm text-muted-foreground">
                No email fits {account?.name} right now. Open one email on the left to preview it.
              </Card>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function AdminOnly({ allowed, children }: { allowed: boolean; children: ReactNode }) {
  if (allowed) return <>{children}</>;
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span tabIndex={0}>{children}</span>
      </TooltipTrigger>
      <TooltipContent>{VIEWER_NO_PERMISSION}</TooltipContent>
    </Tooltip>
  );
}
